import { AgeCard } from 'components/Cards/AgeCard'
import ChainHeaderCard from 'components/Cards/ChainHeaderCard'
import { EducationCard } from 'components/Cards/EducationCard'
import { RaceCard } from 'components/Cards/RaceCard'
import { ZipCodeDemographics } from 'components/Cards/ZipCodeDemographics'
import { CompanyAttributesManager } from 'components/CompanyAttrsManager/CompanyAttrsManager'
import NotFoundCard from 'components/NotFoundCard/NotFoundCard'
import useDocumentTitle from 'components/useDocumentTitle'
import React, { useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import * as S from './styles'
import ContactsTableSection from '../../../../components/Cards/ContactsTableSection'
import DealsSection from '../../../../components/Deals/DealsSection/DealsSection'
import NotesSection from '../../../../components/Notes/NotesSection/NotesSection'
import { Block } from '../../../UI/Block'
import { useIsQueryResultNotFound } from 'utils/useIsQueryResultNotFound'
import { AddressInfo } from 'models/restaurant'
import { FBPage } from '../../../FbUI/Page/FBPage'
import apiService from 'services/api'
import EmailsTableSection from 'components/Cards/EmailsTableSection'
import { featureFlagService } from 'utils/featureFlagService'
import { CompanyTypeSlug, ContactCompany } from 'models/companies'
import { TableTypeSlug } from 'models/companies'
import { TabbedDoorSection } from 'components/Cards/DoorSections'
import { UnmatchedOnlyDoorSection } from 'components/Cards/UnmatchedOnlyDoorSection'
import { ChainData, SubCategoryInfo } from 'components/Cards/DoorSections/types'
import { ComponentType } from 'react'

interface DoorSectionConfig {
  type: 'tabbed' | 'unmatched-only'
  forChainProxyId?: number
  chainID?: number // For restaurants
  chainData?: ChainData
  subCategory: SubCategoryInfo
  matchedTableComponent?: ComponentType<any>
  matchedTableProps?: Record<string, any>
  matchedTableKey?: string
  unmatchedTableKeyPrefix: string
}

interface CommonPageProps<T> {
  uniqueCompanyTypeId?: number
  useGetCompany: {
    companyData?: T
    contactCompanyData?: ContactCompany
    isLoading: boolean
    isError: boolean
    error: Error | null
  }
  tableTypeSlug: TableTypeSlug
  companyTypeSlug: CompanyTypeSlug
  getFieldsFromData?: (data: T) => Partial<{
    contactCompanyId: number
    companyName: string
    websiteAddress: string
    phone?: string
    address: {
      addressUrl?: string
      addressInfo: {
        fullAddress?: string
        city?: string
        state?: string
        zipcode?: string
        extra?: string
      }
      fullAddressInfo?: AddressInfo
    }
    userCreated?: boolean
    doorProxyId?: number
  }>
  breadcrumbs?: { title: string; breadcrumb?: string; url?: string }[]
  customBottomLeftItems?: { header: string; text: string }[]
  customSectionItems?: React.ReactNode
  doorSectionConfig?: DoorSectionConfig
}

export default function CommonPage<T>({
  uniqueCompanyTypeId,
  tableTypeSlug,
  companyTypeSlug,
  useGetCompany: { companyData, contactCompanyData, isLoading, isError, error },
  getFieldsFromData,
  breadcrumbs,
  customBottomLeftItems,
  customSectionItems,
  doorSectionConfig,
}: CommonPageProps<T>) {
  const api = apiService()
  const notFound = useIsQueryResultNotFound({ error, isError })
  const { data: categories } = api.useGetCompanyCategories()
  const { enableEmailIntegration } = featureFlagService()

  const { category, subCategory } = useMemo(() => {
    if (categories) {
      for (const category of categories) {
        for (const subCategory of category.sub_categories) {
          if (subCategory.slug === companyTypeSlug) {
            return { category: category.name, subCategory: subCategory.name }
          }
        }
      }
    }
    return { category: '', subCategory: '' }
  }, [categories, companyTypeSlug])

  if (notFound || !uniqueCompanyTypeId) {
    return <NotFoundCard />
  }

  const companyMappedFields = useMemo(() => {
    if (companyData && getFieldsFromData) {
      return getFieldsFromData(companyData)
    }
  }, [companyData, getFieldsFromData])

  const getCompanyTypeNameFromSlug = (slug: TableTypeSlug) => {
    const companyTypeSlugMap: Partial<Record<TableTypeSlug, string>> = {
      'education-cu': 'University',
      'education-k-12': 'K-12',
      'healthcare-hospitals': 'Hospital',
      'healthcare-nursinghomes': 'Nursing Home',
      'restaurants-bars': 'Restaurant',
      'lodging-hospitality': 'Lodging & Hospitality',
      'lodging-hospitality-doors': 'Lodging & Hospitality Door',
      convenience: 'Convenience Store',
      'convenience-doors': 'Convenience Store Door',
    }
    return companyTypeSlugMap[slug]
  }

  useDocumentTitle(
    `${companyMappedFields?.companyName || 'Not Found'} (${getCompanyTypeNameFromSlug(companyTypeSlug)}) - First Bite`
  )

  if (isLoading) {
    return (
      <div className={'flex items-center justify-center h-96'}>
        <Spinner animation="border" />
      </div>
    )
  }

  const columns = { default: 1, lg: 2, xl: 2, xxl: 4 }

  return (
    <FBPage className="gap-y-4" noContainer>
      {companyMappedFields ? (
        <>
          <ChainHeaderCard
            tableTypeSlug={tableTypeSlug}
            companyTypeSlug={companyTypeSlug}
            contactCompanyId={companyMappedFields.contactCompanyId}
            contactCompany={contactCompanyData}
            isLoading={isLoading}
            category={category}
            subCategory={subCategory}
            domainField={companyMappedFields?.websiteAddress?.trim()}
            name={companyMappedFields?.companyName}
            breadcrumbs={breadcrumbs}
            phone={companyMappedFields?.phone}
            address={{
              url: companyMappedFields?.address?.addressUrl,
              addressInfo: companyMappedFields?.address?.addressInfo,
            }}
            isDoor={!!companyMappedFields.doorProxyId}
            doorId={companyMappedFields.doorProxyId}
            doorProxyId={companyMappedFields.doorProxyId}
            rightColumnHeader={
              <CompanyAttributesManager
                contactCompanyID={
                  companyMappedFields.doorProxyId
                    ? undefined
                    : companyMappedFields.contactCompanyId
                }
                doorProxyId={companyMappedFields.doorProxyId}
                isDoorPage={!!companyMappedFields.doorProxyId}
              />
            }
            customBottomLeftItems={customBottomLeftItems}
          />

          {companyMappedFields?.address && (
            <Block className="mb-3">
              <S.Section columns={columns}>
                <S.SectionItem columns={columns}>
                  <ZipCodeDemographics
                    addressInfo={companyMappedFields?.address?.fullAddressInfo}
                  />
                </S.SectionItem>

                <S.SectionItem columns={columns}>
                  <RaceCard
                    addressInfo={companyMappedFields?.address?.fullAddressInfo}
                  />
                </S.SectionItem>

                <S.SectionItem columns={columns}>
                  <AgeCard
                    addressInfo={companyMappedFields?.address?.fullAddressInfo}
                  />
                </S.SectionItem>

                <S.SectionItem columns={columns}>
                  <EducationCard
                    addressInfo={companyMappedFields?.address?.fullAddressInfo}
                  />
                </S.SectionItem>
              </S.Section>
            </Block>
          )}
        </>
      ) : (
        <div className={'flex items-center justify-center h-96'}>
          <Spinner animation="border" />
        </div>
      )}

      {companyMappedFields && (
        <NotesSection
          contactCompanyId={
            !companyMappedFields.doorProxyId
              ? companyMappedFields.contactCompanyId
              : undefined
          }
          doorProxyId={companyMappedFields.doorProxyId}
          pagination={true}
        />
      )}

      {contactCompanyData && (
        <DealsSection
          forCompany={contactCompanyData}
          forDoorProxyId={companyMappedFields?.doorProxyId}
          pagination={false}
          companyTypeSlug={companyTypeSlug}
          tableTypeSlug={tableTypeSlug}
        />
      )}

      {contactCompanyData && (
        <ContactsTableSection
          contactCompany={contactCompanyData}
          forDoorProxyId={companyMappedFields?.doorProxyId}
        />
      )}

      {contactCompanyData && enableEmailIntegration && (
        <EmailsTableSection
          forChainProxyId={contactCompanyData.id}
          forDoorProxyId={companyMappedFields?.doorProxyId}
        />
      )}

      {doorSectionConfig && (
        <>
          {doorSectionConfig.type === 'tabbed' &&
          doorSectionConfig.matchedTableComponent ? (
            <TabbedDoorSection
              forChainProxyId={doorSectionConfig.forChainProxyId}
              chainID={doorSectionConfig.chainID}
              chainData={doorSectionConfig.chainData}
              subCategory={doorSectionConfig.subCategory}
              matchedTableComponent={doorSectionConfig.matchedTableComponent}
              matchedTableProps={doorSectionConfig.matchedTableProps}
              matchedTableKey={doorSectionConfig.matchedTableKey}
              unmatchedTableKeyPrefix={
                doorSectionConfig.unmatchedTableKeyPrefix
              }
            />
          ) : doorSectionConfig.type === 'unmatched-only' ? (
            <UnmatchedOnlyDoorSection
              forChainProxyId={doorSectionConfig.forChainProxyId!}
              chainData={doorSectionConfig.chainData}
              subCategory={doorSectionConfig.subCategory}
              unmatchedTableKeyPrefix={
                doorSectionConfig.unmatchedTableKeyPrefix
              }
            />
          ) : null}
        </>
      )}

      {customSectionItems && customSectionItems}
    </FBPage>
  )
}
